import type { SortConfig } from '@/types/list.types';

/**
 * Convertit une pagination page/limit en skip/take (si nécessaire pour API legacy)
 */
export function paginationToSkipTake(page: number, limit: number) {
  return {
    skip: (page - 1) * limit,
    take: limit,
  };
}

/**
 * Convertit un skip/take en page/limit
 */
export function skipTakeToPagination(skip: number, take: number) {
  return {
    page: Math.floor(skip / take) + 1,
    limit: take,
  };
}

/**
 * Calcule le nombre total de pages
 */
export function calculateTotalPages(total: number, limit: number): number {
  return Math.ceil(total / limit);
}

/**
 * Compte le nombre de filtres actifs (en excluant les valeurs par défaut)
 */
export function getActiveFiltersCount<T extends Record<string, unknown>>(
  filters: T,
  defaults: Partial<T>,
  excludeKeys: (keyof T)[] = ['page', 'limit', 'sortBy', 'sortDirection']
): number {
  return Object.keys(filters).filter((key) => {
    if (excludeKeys.includes(key as keyof T)) return false;

    const value = filters[key as keyof T];
    const defaultValue = defaults[key as keyof T];

    // Valeur vide
    if (value === '' || value === null || value === undefined) return false;

    // Valeur identique au défaut
    if (value === defaultValue) return false;

    return true;
  }).length;
}

/**
 * Tri client-side d'un tableau
 */
export function sortData<T>(
  data: T[],
  sortConfig: SortConfig,
  accessor?: (item: T, key: string) => unknown
): T[] {
  const { sortBy, sortDirection } = sortConfig;

  return [...data].sort((a, b) => {
    const aValue = accessor ? accessor(a, sortBy) : (a as Record<string, unknown>)[sortBy];
    const bValue = accessor ? accessor(b, sortBy) : (b as Record<string, unknown>)[sortBy];

    // Gestion des valeurs nulles/undefined
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    // Comparaison
    let comparison = 0;
    if (aValue > bValue) {
      comparison = 1;
    } else if (aValue < bValue) {
      comparison = -1;
    }

    return sortDirection === 'DESC' ? -comparison : comparison;
  });
}

/**
 * Filtre client-side d'un tableau par recherche textuelle
 */
export function filterBySearchTerm<T>(
  data: T[],
  searchTerm: string,
  searchableFields: (keyof T)[]
): T[] {
  if (!searchTerm.trim()) return data;

  const lowerSearch = searchTerm.toLowerCase().trim();

  return data.filter((item) => {
    return searchableFields.some((field) => {
      const value = item[field];
      if (value === null || value === undefined) return false;
      return String(value).toLowerCase().includes(lowerSearch);
    });
  });
}

/**
 * Vérifie si on est sur la première page
 */
export function isFirstPage(page: number): boolean {
  return page <= 1;
}

/**
 * Vérifie si on est sur la dernière page
 */
export function isLastPage(page: number, total: number, limit: number): boolean {
  return page >= calculateTotalPages(total, limit);
}
